// Phase 1 — Application shell. See docs/02-information-architecture.md §2.3.
//
// Layout route wrapper: renders the sidebar + topbar around <Outlet/> (the
// existing routed screens). Key coexistence rules:
//   * `.mz` (design-system reset) is applied ONLY to the sidebar/topbar, never
//     to <main>, so legacy antd/MUI screen bodies render pixel-identical.
//   * The auth check mirrors PrivateNavigates so shell chrome never flashes for
//     logged-out users. Route paths/guards are unchanged.
import * as React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { getAuthToken } from "../Utils/localStorage";
import { AppSidebar } from "./app-sidebar";
import { Topbar } from "./topbar";
import { Sheet, SheetContent } from "ui/sheet";

const COLLAPSED_KEY = "mz-sidebar-collapsed";

export default function AppShell() {
  const location = useLocation();
  const authed = !!getAuthToken();

  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch (ex) {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const toggleCollapse = () =>
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      } catch (ex) {
        /* ignore */
      }
      return next;
    });

  // Close the mobile drawer whenever the route changes.
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (!authed) return <Navigate to="/login" replace />;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <AppSidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </div>

      {/* Mobile sidebar (drawer) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[260px] p-0">
          <AppSidebar mobile onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenMobileNav={() => setMobileOpen(true)} />
        {/* No `.mz` here: legacy screen bodies keep their own antd/MUI styling. */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
